import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { formatErr } from "../../lib/api";
import { Save, Plus, X } from "lucide-react";

const EXERCISE_OPTIONS = [
    "push-up", "pull-up", "squat", "lunge", "burpee", "plank", "crunch",
    "jumping-jack", "mountain-climber", "weighted-squat",
];

export default function AdminInviteQuotas() {
    const [tiers, setTiers] = useState([]);

    const load = async () => {
        const r = await client.get("/admin/invite-quotas");
        setTiers(r.data || []);
    };
    useEffect(() => { load(); }, []);

    const update = (idx, patch) => {
        const next = [...tiers];
        next[idx] = { ...next[idx], ...patch };
        setTiers(next);
    };

    const updateExercise = (idx, listKey, i, patch) => {
        const arr = [...(tiers[idx][listKey] || [])];
        arr[i] = { ...arr[i], ...patch };
        update(idx, { [listKey]: arr });
    };

    const addExercise = (idx, listKey) => {
        const arr = [...(tiers[idx][listKey] || []), { exercise_type: "push-up", reps_required: 10 }];
        update(idx, { [listKey]: arr });
    };

    const removeExercise = (idx, listKey, i) => {
        const arr = [...(tiers[idx][listKey] || [])];
        arr.splice(i, 1);
        update(idx, { [listKey]: arr });
    };

    const save = async (tier) => {
        try {
            await client.put(`/admin/invite-quotas/${tier.id}`, {
                host_exercises: tier.host_exercises,
                recipient_exercises: tier.recipient_exercises,
                level_min: tier.level_min,
                level_max: tier.level_max,
            });
            toast.success(`${tier.tier_name} saved`);
            await load();
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    return (
        <div data-testid="admin-invite-quotas-page">
            <div className="mb-6">
                <div className="label-cap text-xs text-neon-green">Admin</div>
                <h1 className="font-display font-black text-3xl uppercase tracking-tight">Invite Quotas</h1>
                <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                    Set the exercises a host must complete to ARM an invite code, and the proof exercises the recipient
                    must complete within 1 hour to redeem it. Quotas auto-apply by host's current level.
                </p>
            </div>

            <div className="space-y-6">
                {tiers.map((t, idx) => (
                    <div key={t.id} className="surface-1 p-4" data-testid={`tier-${t.id}`}>
                        <div className="flex items-end justify-between mb-3">
                            <div>
                                <div className="label-cap text-[10px] text-neon-green">{t.id}</div>
                                <div className="font-display font-bold text-lg uppercase">{t.tier_name}</div>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-zinc-500">Levels</span>
                                <input
                                    type="number"
                                    value={t.level_min}
                                    onChange={(e) => update(idx, { level_min: Number(e.target.value) })}
                                    data-testid={`tier-${t.id}-min`}
                                    className="w-14 bg-charcoal-900 border border-charcoal-500 px-2 py-1 text-center"
                                />
                                <span className="text-zinc-500">–</span>
                                <input
                                    type="number"
                                    value={t.level_max}
                                    onChange={(e) => update(idx, { level_max: Number(e.target.value) })}
                                    data-testid={`tier-${t.id}-max`}
                                    className="w-14 bg-charcoal-900 border border-charcoal-500 px-2 py-1 text-center"
                                />
                            </div>
                        </div>

                        <ExerciseList
                            label="Host must complete"
                            tierId={t.id}
                            listKey="host_exercises"
                            list={t.host_exercises}
                            onChange={(i, patch) => updateExercise(idx, "host_exercises", i, patch)}
                            onAdd={() => addExercise(idx, "host_exercises")}
                            onRemove={(i) => removeExercise(idx, "host_exercises", i)}
                        />

                        <ExerciseList
                            label="Recipient must prove (within 1h)"
                            tierId={t.id}
                            listKey="recipient_exercises"
                            list={t.recipient_exercises}
                            onChange={(i, patch) => updateExercise(idx, "recipient_exercises", i, patch)}
                            onAdd={() => addExercise(idx, "recipient_exercises")}
                            onRemove={(i) => removeExercise(idx, "recipient_exercises", i)}
                        />

                        <button
                            onClick={() => save(t)}
                            data-testid={`tier-${t.id}-save`}
                            className="mt-4 btn-neon px-4 py-2 flex items-center gap-2 text-sm"
                        >
                            <Save size={14} /> Save {t.id}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ExerciseList({ label, tierId, listKey, list, onChange, onAdd, onRemove }) {
    return (
        <div className="mt-4">
            <div className="label-cap text-[11px] text-zinc-400 mb-2">{label}</div>
            <div className="space-y-2">
                {(list || []).map((it, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`${tierId}-${listKey}-${i}`}>
                        <select
                            value={it.exercise_type}
                            onChange={(e) => onChange(i, { exercise_type: e.target.value })}
                            className="bg-charcoal-900 border border-charcoal-500 px-3 py-1.5 flex-1"
                        >
                            {EXERCISE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        <input
                            type="number"
                            min={1}
                            value={it.reps_required}
                            onChange={(e) => onChange(i, { reps_required: Number(e.target.value) })}
                            className="w-20 bg-charcoal-900 border border-charcoal-500 px-2 py-1.5 text-center"
                        />
                        <span className="text-xs text-zinc-500">reps</span>
                        <button
                            type="button"
                            onClick={() => onRemove(i)}
                            className="p-1.5 text-zinc-400 hover:text-neon-orange"
                            aria-label="remove"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={onAdd}
                    className="text-xs text-neon-green flex items-center gap-1 mt-1 hover:underline"
                >
                    <Plus size={12} /> Add exercise
                </button>
            </div>
        </div>
    );
}
